import React, { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { useTypewriter } from "react-simple-typewriter";
import { NavLink } from "react-router-dom";
import { FiArrowRight, FiGithub, FiLinkedin, FiTwitter } from "react-icons/fi";
import { useLanguage } from "../Context/LanguageContext";
import hebraheem from "../Assets/headshot.png";
import "./home.css";

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const techStack = [
  { label: "React", color: "#61DAFB", delay: 1.0, top: "5%", right: "-10%" },
  { label: "Node.js", color: "#68A063", delay: 1.2, bottom: "5%", right: "-12%" },
  { label: "TypeScript", color: "#3178C6", delay: 1.4, top: "35%", left: "-18%" },
  { label: "JavaScript", color: "#F7DF1E", delay: 1.6, top: "10%", left: "-5%" },
  { label: "Angular", color: "#DD0031", delay: 1.8, bottom: "30%", left: "-15%" },
  { label: "Vue", color: "#42B883", delay: 2.0, bottom: "0%", left: "5%" },
  { label: "Next.js", color: "#E5E9F0", delay: 2.2, top: "55%", right: "-18%" },
  { label: "NestJS", color: "#E0234E", delay: 2.4, bottom: "45%", right: "-5%" },
  { label: "Express", color: "#F7DF1E", delay: 2.6, top: "75%", left: "-10%" },
];

const MagneticButton = ({ children, className, ...props }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width / 2);
    const dy = e.clientY - (rect.top + rect.height / 2);
    x.set(dx * 0.2);
    y.set(dy * 0.2);
  };
  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      style={{ x: springX, y: springY, display: "inline-block" }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <motion.div
        className={className}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        {...props}
      >
        {children}
      </motion.div>
    </motion.div>
  );
};

const FloatingOrb = ({ style, animClass }) => <div className={`orb ${animClass}`} style={style} />;

const CodeSnippet = ({ t }) => (
  <motion.div
    className="hero__code-window"
    initial={{ opacity: 0, y: 40, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay: 1, duration: 0.8, ease: "easeOut" }}
  >
    <div className="code-window__header">
      <span className="dot dot--red" />
      <span className="dot dot--yellow" />
      <span className="dot dot--green" />
      <span className="code-window__title">{t("HomeCodeTitle")}</span>
    </div>
    <div className="code-window__body">
      <div className="code-line">
        <span className="ln">1</span>
        <span className="kw">const</span> <span className="fn">{t("HomeCodeDev")}</span>{" "}
        <span className="op">=</span> <span className="br">{"{"}</span>
      </div>
      <div className="code-line">
        <span className="ln">2</span> <span className="prop">name</span>
        <span className="op">:</span> <span className="str">"{t("HomeCodeName")}"</span>
        <span className="op">,</span>
      </div>
      <div className="code-line">
        <span className="ln">3</span> <span className="prop">role</span>
        <span className="op">:</span> <span className="str">"{t("HomeCodeRole")}"</span>
        <span className="op">,</span>
      </div>
      <div className="code-line">
        <span className="ln">4</span> <span className="prop">stack</span>
        <span className="op">:</span> <span className="br">[</span>
        <span className="str">"{t("HomeCodeStack.0")}"</span>
        <span className="op">, </span>
        <span className="str">"{t("HomeCodeStack.1")}"</span>
        <span className="op">, </span>
        <span className="str">"{t("HomeCodeStack.2")}"</span>
        <span className="br">]</span>
        <span className="op">,</span>
      </div>
      <div className="code-line">
        <span className="ln">5</span> <span className="prop">available</span>
        <span className="op">:</span> <span className="kw">{t("HomeCodeAvailable")}</span>
      </div>
      <div className="code-line">
        <span className="ln">6</span>
        <span className="br">{"}"}</span>
        <span className="op">;</span>
      </div>
      <div className="code-line code-line--cursor">
        <span className="ln">7</span>
      </div>
    </div>
  </motion.div>
);

const Home = () => {
  const { t, language } = useLanguage();
  const { text } = useTypewriter({
    words: ["FullStack-Developer", "Freelancer", "Problem-Solver", "AI-Enthusiast"],
    loop: true,
    cursorStyle: "_",
    typeSpeed: 70,
    deleteSpeed: 50,
    delaySpeed: 2000,
  });

  return (
    <div className="home" key={language}>
      {/* Background orbs */}
      <FloatingOrb
        animClass="orb--1"
        style={{
          width: 500,
          height: 500,
          top: "-10%",
          left: "-10%",
          background: "radial-gradient(circle, rgba(77,91,206,0.15) 0%, transparent 70%)",
        }}
      />
      <FloatingOrb
        animClass="orb--2"
        style={{
          width: 400,
          height: 400,
          bottom: "5%",
          right: "-5%",
          background: "radial-gradient(circle, rgba(67,217,173,0.12) 0%, transparent 70%)",
        }}
      />
      <FloatingOrb
        animClass="orb--3"
        style={{
          width: 300,
          height: 300,
          top: "50%",
          left: "30%",
          background: "radial-gradient(circle, rgba(233,146,135,0.08) 0%, transparent 70%)",
        }}
      />

      <div className="home__inner">
        {/* Left: Text content */}
        <motion.div
          className="home__content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="home__greeting mono">
            <span style={{ color: "var(--text-muted)" }}>{`// `}</span>
            <span style={{ color: "var(--accent-cyan)" }}>{t("HomeGreeting")}</span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="home__name">
            {t("HomeName")}
            <br />
            <span className="home__name--accent">{t("HomeSurname")}</span>
          </motion.h1>

          <motion.div variants={itemVariants} className="home__typewriter mono">
            <span style={{ color: "var(--accent-purple)" }}>{`> `}</span>
            <span style={{ color: "var(--accent-cyan)" }}>{text}</span>
            <span className="cursor-blink" style={{ color: "var(--accent-cyan)" }} />
          </motion.div>

          <motion.p variants={itemVariants} className="home__bio">
            {t("HomeBio")}
          </motion.p>

          <motion.div variants={itemVariants} className="home__github mono">
            <span style={{ color: "var(--text-muted)" }}>const </span>
            <span style={{ color: "var(--accent-purple)" }}>github</span>
            <span style={{ color: "var(--text-primary)" }}> = </span>
            <a
              href="https://github.com/hebraheem"
              target="_blank"
              rel="noreferrer"
              style={{ color: "var(--accent-cyan)" }}
            >
              "https://github.com/hebraheem"
            </a>
          </motion.div>

          <motion.div variants={itemVariants} className="home__ctas">
            <NavLink to="/projects">
              <MagneticButton className="btn btn--primary">
                {t("ViewProject")} <FiArrowRight size={16} />
              </MagneticButton>
            </NavLink>
            <NavLink to="/contact">
              <MagneticButton className="btn btn--outline">{t("_contact")}</MagneticButton>
            </NavLink>
          </motion.div>

          <motion.div variants={itemVariants} className="home__socials">
            {[
              {
                href: "https://github.com/hebraheem",
                icon: <FiGithub size={20} />,
                color: "#E5E9F0",
              },
              {
                href: "https://www.linkedin.com/in/ibrahim-adebiyi-b7b979166/",
                icon: <FiLinkedin size={20} />,
                color: "#0077B5",
              },
              {
                href: "https://twitter.com/just_hebraheem",
                icon: <FiTwitter size={20} />,
                color: "#1DA1F2",
              },
            ].map(({ href, icon, color }) => (
              <motion.a
                key={href}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="home__social-link"
                whileHover={{
                  scale: 1.25,
                  color: color,
                  boxShadow: `0 0 15px ${color}33`,
                  transition: { type: "spring", stiffness: 400, damping: 10 },
                }}
                whileTap={{ scale: 0.9 }}
              >
                {icon}
              </motion.a>
            ))}
          </motion.div>
        </motion.div>

        {/* Right: Photo + Code window */}
        <div className="home__visual">
          <motion.div
            className="home__photo-wrapper"
            initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
          >
            <div className="home__photo-ring" />
            <img
              src={hebraheem}
              alt={`${t("HomeName")} ${t("HomeSurname")}`}
              className="home__photo"
            />
            <div className="home__photo-glow" />

            {/* Floating skill badges */}
            {techStack.map(({ label, color, delay, ...pos }) => (
              <motion.div
                key={label}
                className="home__badge"
                style={{ ...pos, borderColor: color }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay, type: "spring", stiffness: 200 }}
                whileHover={{
                  scale: 1.15,
                  boxShadow: `0 0 20px ${color}44`,
                  borderColor: color,
                  transition: { duration: 0.2 },
                }}
              >
                <span style={{ color }}>{label}</span>
              </motion.div>
            ))}
          </motion.div>

          <CodeSnippet t={t} />
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="home__scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        <motion.div
          className="home__scroll-dot"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </div>
  );
};

export default Home;
